/**
 * AutomationDocumentation — the long-form documentation prose at the bottom of
 * the Overview tab: `ahoy_documentation`, the "How it works" written for
 * non-technical users (headings and all — the built-ins already author it
 * with `# How It Works` sections).
 *
 * Renders HEADERLESS: no "Documentation" section chrome — the prose's own
 * headings carry the structure, and the divider the settings block leaves
 * above is the only separator. Unclamped (`noClamp`): the prose is the tail
 * of the tab, so a Show-more toggle would hide content with nothing below it.
 *
 * Diffs when a pending proposal rewrites it — this section is the only place
 * the property renders (`hiddenProperties`), so without the diff a rewritten
 * write-up would look like one that had always said that.
 *
 * Renders nothing when the property is empty.
 */

import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { useRecordDraft } from '../contexts/RecordDraftContext';
import { MarkdownPropertyBlock } from './markdown-property-block';

export function AutomationDocumentation({ object }: { object: CrmObject }) {
  const documentation = getPropertyValue<string>(
    object.properties?.ahoy_documentation
  );
  const { schema } = usePropertySchema(object.org_id, object.entity_type_name);
  const draft = useRecordDraft();

  const previousValues = draft?.previousValues;
  const isChanged = previousValues
    ? 'ahoy_documentation' in previousValues
    : false;

  if (!documentation) {
    return null;
  }

  return (
    <MarkdownPropertyBlock
      value={documentation}
      previousValue={previousValues?.['ahoy_documentation']}
      hasPrevious={isChanged}
      schema={schema['ahoy_documentation']}
      orgId={object.org_id}
      entityProperties={object.properties}
      collapseKey={`${object.id}:documentation:${documentation}`}
      noClamp
    />
  );
}
